import type { ReactNode } from 'react';
import { Card } from '@wundo/ui';
import type { Cohort } from '@/types/cohorts.ts';
import { formatDate, isoToDateInput } from '@/utils/dateUtils.ts';
import { pilotExplanation } from '@/components/cohorts/pilotExplanation.ts';

/*
 * A definition list rather than SettingsRow: these are facts about the cohort,
 * not settings this page can change — editing happens on the form. `dt`/`dd`
 * are what a description list is made of, so the label carries SectionLabel's
 * look rather than SectionLabel itself, which cannot render as a `dt`.
 */
function Fact({
  term,
  children,
  wide,
}: {
  term: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={wide ? 'col-span-2 max-mobile:col-span-1' : undefined}>
      <dt className='text-xs font-bold tracking-widest text-ink-muted uppercase'>
        {term}
      </dt>
      <dd className='mt-1 text-base whitespace-pre-wrap text-ink'>{children}</dd>
    </div>
  );
}

/** What the cohort is, in the facts the form sets. */
export function CohortDetailsTab({ cohort }: { cohort: Cohort }) {
  const startAt = isoToDateInput(cohort.start_at);

  return (
    <Card>
      <dl className='grid grid-cols-2 gap-5 max-mobile:grid-cols-1'>
        <Fact term='Title'>{cohort.title}</Fact>
        <Fact term='Start Date'>{formatDate(cohort.start_at)}</Fact>
        <Fact term='Pilot'>{cohort.pilot ? 'Yes' : 'No'}</Fact>
        {cohort.pilot && <Fact term='Weeks'>{cohort.weeks}</Fact>}
        {cohort.pilot && startAt !== '' && (
          <Fact term='Schedule' wide>
            {pilotExplanation(startAt, cohort.weeks)}
          </Fact>
        )}
        {cohort.welcome_message && (
          <Fact term='Welcome Message' wide>
            {cohort.welcome_message}
          </Fact>
        )}
      </dl>
    </Card>
  );
}
